import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import api from './api';

const tarihFormatla = (deger) => {
  if (!deger) return '-';
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return '-';
  return tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

// "3 ay önce açıldı" gibi bir ifade; tarihin tek başına söylemediği şeyi söyler.
const gecenSure = (deger) => {
  if (!deger) return null;
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return null;
  const gun = Math.floor((Date.now() - tarih.getTime()) / 86400000);
  if (gun < 0) return null;
  if (gun === 0) return 'bugün';
  if (gun === 1) return 'dün';
  if (gun < 30) return `${gun} gün önce`;
  if (gun < 365) return `${Math.floor(gun / 30)} ay önce`;
  return `${Math.floor(gun / 365)} yıl önce`;
};

const abonelikDurumu = (kalanGun) => {
  if (kalanGun === null || kalanGun === undefined) return { metin: 'Süresiz', renk: 'secondary' };
  if (kalanGun < 0) return { metin: `${Math.abs(kalanGun)} gün önce doldu`, renk: 'danger' };
  if (kalanGun === 0) return { metin: 'Bugün doluyor', renk: 'danger' };
  if (kalanGun <= 7) return { metin: `${kalanGun} gün kaldı`, renk: 'warning' };
  return { metin: `${kalanGun} gün kaldı`, renk: 'success' };
};

function BilgiSatiri({ etiket, deger }) {
  return (
    <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{etiket}</span>
      <span className="fw-semibold text-end" style={{ color: 'var(--text-main)' }}>{deger}</span>
    </div>
  );
}

function AdminDiyetisyenDetay() {
  const { diyetisyenId } = useParams();
  const navigate = useNavigate();

  const [diyetisyen, setDiyetisyen] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const detayGetir = useCallback(async () => {
    try {
      const res = await api.get(`/admin/kullanicilar/${diyetisyenId}`);
      setDiyetisyen(res.data);
    } catch (err) {
      setHata(err.response?.status === 404
        ? 'Diyetisyen bulunamadı.'
        : 'Diyetisyen bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setYukleniyor(false);
    }
  }, [diyetisyenId]);

  useEffect(() => { detayGetir(); }, [detayGetir]);

  if (yukleniyor) return <Spinner animation="border" variant="light" />;
  if (hata) {
    return (
      <>
        <Alert variant="danger">{hata}</Alert>
        <Button variant="light" onClick={() => navigate('/admin/diyetisyenler')}>Diyetisyen Yönetimine Dön</Button>
      </>
    );
  }
  if (!diyetisyen) return null;

  const durum = abonelikDurumu(diyetisyen.kalan_gun);
  const acilisSuresi = gecenSure(diyetisyen.kayit_tarihi);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <Button variant="outline-light" size="sm" onClick={() => navigate('/admin/diyetisyenler')}>
          ← Diyetisyen Yönetimi
        </Button>
      </div>

      <Card className="glass-card text-white mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h2 className="mb-1">{diyetisyen.kullanici_adi}</h2>
              <div className="d-flex gap-2 flex-wrap">
                <Badge bg={diyetisyen.rol === 'klinik_yoneticisi' ? 'info' : 'primary'}>
                  {diyetisyen.rol === 'klinik_yoneticisi' ? 'Klinik Yöneticisi' : 'Diyetisyen'}
                </Badge>
                <Badge bg={diyetisyen.aktif_mi ? 'success' : 'secondary'}>
                  {diyetisyen.aktif_mi ? 'Aktif' : 'Pasif'}
                </Badge>
                {diyetisyen.is_demo && <Badge bg="warning" text="dark">Demo Hesap</Badge>}
              </div>
            </div>
            <Badge bg={durum.renk} style={{ fontSize: '0.95rem', padding: '0.6rem 0.9rem' }}>
              {durum.metin}
            </Badge>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="glass-card text-white h-100">
            <Card.Body>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Hesabın Açılış Tarihi</div>
              <div className="fw-bolder" style={{ fontSize: '1.6rem' }}>{tarihFormatla(diyetisyen.kayit_tarihi)}</div>
              {acilisSuresi && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{acilisSuresi} açıldı</div>}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="glass-card text-white h-100">
            <Card.Body>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Aboneliğin Bitiş Tarihi</div>
              <div className="fw-bolder" style={{ fontSize: '1.6rem' }}>
                {diyetisyen.abonelik_bitis_tarihi ? tarihFormatla(diyetisyen.abonelik_bitis_tarihi) : 'Belirtilmemiş'}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{durum.metin}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Card className="glass-card text-white h-100">
            <Card.Body>
              <Card.Title as="h5" className="mb-3">Hesap Bilgileri</Card.Title>
              <BilgiSatiri etiket="ID" deger={diyetisyen.id} />
              <BilgiSatiri etiket="E-posta" deger={diyetisyen.eposta || '-'} />
              <BilgiSatiri etiket="Telefon" deger={diyetisyen.telefon || '-'} />
              <BilgiSatiri etiket="Bağlı Olduğu Klinik" deger={diyetisyen.klinik?.ad || 'Bağlı Değil'} />
              {diyetisyen.is_demo && (
                <>
                  <BilgiSatiri etiket="Demo Başlangıcı" deger={tarihFormatla(diyetisyen.demo_baslangic_tarihi)} />
                  <BilgiSatiri
                    etiket="İlk Giriş Şifresi"
                    deger={diyetisyen.sifre_degistirildi ? 'Değiştirildi' : 'Henüz değiştirilmedi'}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="glass-card text-white h-100">
            <Card.Body>
              <Card.Title as="h5" className="mb-3">Abonelik ve Kullanım</Card.Title>
              <BilgiSatiri
                etiket="Paketler"
                deger={diyetisyen.paketler?.length ? diyetisyen.paketler.map((p) => p.ad).join(', ') : 'Paket yok'}
              />
              <BilgiSatiri etiket="Aylık Toplam Ücret" deger={`${(diyetisyen.toplam_ucret || 0).toLocaleString('tr-TR')} ₺`} />
              <BilgiSatiri etiket="Danışan Sayısı" deger={diyetisyen.danisan_sayisi} />
              <BilgiSatiri etiket="Randevu Sayısı" deger={diyetisyen.randevu_sayisi} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default AdminDiyetisyenDetay;
